import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorCode, serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";

// Sổ hậu mãi (spec §6, quyết định 0043): đơn đã trả ảnh nào cần chào mua thêm,
// đã chào mấy lần, chào bằng kênh gì. Kiểu dữ liệu lấy thẳng từ schema sinh ra
// bởi openapi — KHÔNG khai tay, vì khai tay là tự cho phép hai bên lệch nhau.

export type AftercareItem = components["schemas"]["AftercareItem"];
export type AftercarePage = components["schemas"]["AftercarePage"];
export type AftercareRow = components["schemas"]["AftercareRow"];
export type AftercareTouch = components["schemas"]["AftercareTouch"];

/** 7 chip lọc của spec §6.4. Thứ tự này cũng là thứ tự nút trên màn. */
export const AFTERCARE_FILTERS = [
  { id: "chua_gui", label: "Chưa gửi thông tin" },
  { id: "chua_mail", label: "Chưa gửi mail" },
  { id: "da_gui_1", label: "Đã gửi 1 lần" },
  { id: "da_gui_2", label: "Đã gửi 2 lần" },
  { id: "da_gui_3", label: "Đã gửi 3 lần" },
  { id: "da_mua", label: "Đã mua thêm" },
  { id: "dong", label: "Đã đóng" },
] as const;

export type AftercareFilterId = (typeof AFTERCARE_FILTERS)[number]["id"];

/**
 * 5 kênh chào khách. `email` cố ý KHÔNG có ở đây: kênh đó do máy tự ghi sau
 * khi thư gửi thành công, người dùng không khai tay được (chống khai khống
 * "đã gửi mail cho khách"). Thân yêu cầu AftercareTouchInput ở schema cũng
 * không nhận `email`, nên thêm vào đây là đỏ lúc kiểm kiểu.
 */
export const AFTERCARE_CHANNELS = [
  { id: "zalo", label: "Zalo" },
  { id: "call", label: "Gọi điện" },
  { id: "messenger", label: "Messenger" },
  { id: "counter", label: "Tại quầy" },
  { id: "other", label: "Khác" },
] as const;

export type AftercareChannelId = (typeof AFTERCARE_CHANNELS)[number]["id"];

/** 2 lý do đóng người dùng chọn được. `da_chao_du` là lý do MÁY tự đặt khi
 * chạm trần 3 lần chào — không nằm ở đây, và schema cũng không nhận. */
export const AFTERCARE_CLOSE_REASONS = [
  { id: "khong_quan_tam", label: "Khách không quan tâm" },
  { id: "khong_lien_lac_duoc", label: "Không liên lạc được" },
] as const;

export type AftercareCloseReasonId = (typeof AFTERCARE_CLOSE_REASONS)[number]["id"];

export type AftercareQuery = {
  filter: AftercareFilterId;
  branch?: string | undefined;
  limit: number;
  offset: number;
};

export const aftercareKeys = {
  all: ["aftercare"] as const,
  list: (q: AftercareQuery) => ["aftercare", "list", q] as const,
};

/**
 * readAftercareError — máy chủ gói mọi lỗi trong phong bì LỒNG
 * `{"error":{"code","message"}}` (platform/errs/handler.go), nên đọc phẳng
 * `error.message` luôn ra rỗng và mọi 409/503 rơi hết về một câu chung chung.
 *
 * Giữ nguyên lời tiếng Việt của máy chủ vì đó là thứ DUY NHẤT nói cho người
 * bấm biết VÌ SAO: "khách đã mua thêm rồi", "đã chào đủ 3 lần", "đơn này chưa
 * có email khách", "gửi thư không thành công — thử lại sau ít phút". Lời tiếng
 * Anh ("internal error") đi qua bộ dịch chung.
 *
 * Trừ hai mã máy móc: `not_found` (máy chủ trả "order not found" — tiếng Anh,
 * và cố ý dùng 404 thay 403 để không lộ sự tồn tại của đơn chi nhánh khác) và
 * `forbidden`. Hai mã đó có câu riêng của sổ hậu mãi.
 */
export function readAftercareError(error: unknown, fallback: string): string {
  const code = serverErrorCode(error);
  if (code === "not_found") return "Không tìm thấy đơn này trong phạm vi chi nhánh của bạn";
  if (code === "forbidden") return "Bạn không có quyền thao tác trên sổ hậu mãi";
  return serverErrorMessage(error, fallback);
}

export function useAftercareList(q: AftercareQuery) {
  return useQuery({
    queryKey: aftercareKeys.list(q),
    queryFn: async (): Promise<AftercarePage> => {
      // limit/offset đưa thẳng như người gọi truyền: máy chủ đã chuẩn hoá
      // (thiếu → 20, trần 200, offset âm → 0). Chuẩn hoá thêm ở đây là dựng
      // bộ luật thứ hai rồi sớm muộn lệch với bộ luật thật.
      const { data, error } = await api.GET("/aftercare", {
        params: {
          query: {
            filter: q.filter,
            ...(q.branch ? { branch: q.branch } : {}),
            limit: q.limit,
            offset: q.offset,
          },
        },
      });
      if (error || !data) throw new Error(readAftercareError(error, "Không tải được sổ hậu mãi"));
      return data;
    },
  });
}

/** Mọi thao tác ghi đều nạp lại danh sách: chip đếm đổi theo từng lần bấm. */
function useAftercareMutation<TVars>(fn: (v: TVars) => Promise<AftercareRow>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aftercareKeys.all });
    },
  });
}

export function useAftercareTouch() {
  return useAftercareMutation(
    async (v: { orderId: string; channel: AftercareChannelId; note?: string | undefined }) => {
      const { data, error } = await api.POST("/aftercare/{orderId}/touch", {
        params: { path: { orderId: v.orderId } },
        // `exactOptionalPropertyTypes` bật: truyền thẳng `note: undefined` là
        // đỏ, nên chỗ tuỳ chọn dùng dạng trải.
        body: { channel: v.channel, ...(v.note ? { note: v.note } : {}) },
      });
      if (error || !data) {
        throw new Error(readAftercareError(error, "Không ghi được lần gửi thông tin"));
      }
      return data;
    },
  );
}

export function useAftercareEmail() {
  return useAftercareMutation(async (v: { orderId: string }) => {
    const { data, error } = await api.POST("/aftercare/{orderId}/email", {
      params: { path: { orderId: v.orderId } },
    });
    if (error || !data) throw new Error(readAftercareError(error, "Gửi thư không thành công"));
    return data;
  });
}

export function useAftercareClose() {
  return useAftercareMutation(async (v: { orderId: string; reason: AftercareCloseReasonId }) => {
    const { data, error } = await api.POST("/aftercare/{orderId}/close", {
      params: { path: { orderId: v.orderId } },
      body: { reason: v.reason },
    });
    if (error || !data) throw new Error(readAftercareError(error, "Không đóng được"));
    return data;
  });
}

export function useAftercareReopen() {
  return useAftercareMutation(async (v: { orderId: string }) => {
    const { data, error } = await api.POST("/aftercare/{orderId}/reopen", {
      params: { path: { orderId: v.orderId } },
    });
    if (error || !data) throw new Error(readAftercareError(error, "Không mở lại được"));
    return data;
  });
}
